import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
} from '@alertwhale/ui'
import { useApplication, useConfigureApns, useConfigureFcm } from '@/hooks/useApplications'
import { SubscriptionLinkGenerator } from '@/components/SubscriptionLinkGenerator'
import {
  Loader2,
  Settings,
  Key,
  Bell,
  Activity,
  Upload,
  HelpCircle,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Apple,
  Smartphone,
  FileKey,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: app, isLoading } = useApplication(id!)
  const configureApns = useConfigureApns(id!)
  const configureFcm = useConfigureFcm(id!)

  const [apnsDialogOpen, setApnsDialogOpen] = useState(false)
  const [fcmDialogOpen, setFcmDialogOpen] = useState(false)
  const [apnsHelpOpen, setApnsHelpOpen] = useState(false)
  const [fcmHelpOpen, setFcmHelpOpen] = useState(false)

  // APNs form state
  const [apnsKeyId, setApnsKeyId] = useState('')
  const [apnsTeamId, setApnsTeamId] = useState('')
  const [apnsKeyFile, setApnsKeyFile] = useState<File | null>(null)

  // FCM form state
  const [fcmFile, setFcmFile] = useState<File | null>(null)

  const handleApnsSubmit = async () => {
    if (!apnsKeyId || !apnsTeamId || !apnsKeyFile) {
      toast.error('Please fill all required fields')
      return
    }

    try {
      const keyContent = await apnsKeyFile.text()
      await configureApns.mutateAsync({
        keyId: apnsKeyId,
        teamId: apnsTeamId,
        key: keyContent,
      })
      toast.success('APNs credentials configured successfully')
      setApnsDialogOpen(false)
      setApnsKeyId('')
      setApnsTeamId('')
      setApnsKeyFile(null)
    } catch {
      toast.error('Failed to configure APNs credentials')
    }
  }

  const handleFcmSubmit = async () => {
    if (!fcmFile) {
      toast.error('Please select a service account JSON file')
      return
    }

    try {
      const content = await fcmFile.text()
      // Validate it's valid JSON
      JSON.parse(content)
      await configureFcm.mutateAsync({
        credentials: content,
      })
      toast.success('FCM credentials configured successfully')
      setFcmDialogOpen(false)
      setFcmFile(null)
    } catch {
      toast.error('Failed to configure FCM credentials. Make sure the file is valid JSON.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!app) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Application not found</h2>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{app.name}</h1>
          <p className="text-muted-foreground">Application ID: {app.id}</p>
        </div>
        <Button variant="outline">
          <Settings className="h-4 w-4" />
          Settings
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-secondary/50 p-1 border border-border/50">
          <TabsTrigger
            value="overview"
            className="cursor-pointer hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_0_12px_hsla(187,92%,55%,0.3)]"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="credentials"
            className="cursor-pointer hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_0_12px_hsla(187,92%,55%,0.3)]"
          >
            Credentials
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="cursor-pointer hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_0_12px_hsla(187,92%,55%,0.3)]"
          >
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="live-sessions"
            className="cursor-pointer hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_0_12px_hsla(187,92%,55%,0.3)]"
          >
            Live Sessions
          </TabsTrigger>
          <TabsTrigger
            value="developer"
            className="cursor-pointer hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_0_12px_hsla(187,92%,55%,0.3)]"
          >
            Developer Tools
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Platform Configuration</CardTitle>
                <CardDescription>iOS and Android settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium">iOS Bundle ID</p>
                  <p className="text-sm text-muted-foreground">
                    {app.ios_bundle_id || 'Not configured'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Android Package Name</p>
                  <p className="text-sm text-muted-foreground">
                    {app.android_package_name || 'Not configured'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Push Credentials</CardTitle>
                <CardDescription>APNs and FCM configuration status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Apple Push (APNs)</span>
                  {app.apns_key_id ? (
                    <Badge variant="success">Configured</Badge>
                  ) : (
                    <Badge variant="outline">Not configured</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Firebase (FCM)</span>
                  {app.fcm_credentials_encrypted ? (
                    <Badge variant="success">Configured</Badge>
                  ) : (
                    <Badge variant="outline">Not configured</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Bell className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Notifications Today</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Activity className="h-6 w-6 mx-auto mb-2 text-success" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Active Sessions</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Key className="h-6 w-6 mx-auto mb-2 text-accent" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">API Keys</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Smartphone className="h-6 w-6 mx-auto mb-2 text-warning" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Registered Devices</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credentials" className="space-y-4 mt-4">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <Apple className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle>Apple Push Notification Service (APNs)</CardTitle>
                    <CardDescription>
                      Configure your APNs credentials for iOS push notifications
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setApnsHelpOpen(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  How to get credentials
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {app.apns_key_id ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-success">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">APNs Configured</span>
                  </div>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">Key ID</span>
                      <span className="font-mono">{app.apns_key_id}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Team ID</span>
                      <span className="font-mono">{app.apns_team_id}</span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setApnsDialogOpen(true)}>
                    Update Credentials
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <AlertCircle className="h-5 w-5" />
                    <span>APNs not configured - iOS notifications are disabled</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Upload your APNs authentication key (.p8 file) to enable iOS push notifications.
                  </p>
                  <Button onClick={() => setApnsDialogOpen(true)}>
                    <FileKey className="h-4 w-4" />
                    Configure APNs
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle>Firebase Cloud Messaging (FCM)</CardTitle>
                    <CardDescription>
                      Configure your FCM credentials for Android push notifications
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFcmHelpOpen(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  How to get credentials
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {app.fcm_credentials_encrypted ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-success">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">FCM Configured</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Your Firebase service account credentials are securely stored.
                  </p>
                  <Button variant="outline" onClick={() => setFcmDialogOpen(true)}>
                    Update Credentials
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <AlertCircle className="h-5 w-5" />
                    <span>FCM not configured - Android notifications are disabled</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Upload your Firebase service account JSON to enable Android push notifications.
                  </p>
                  <Button onClick={() => setFcmDialogOpen(true)}>
                    <FileKey className="h-4 w-4" />
                    Configure FCM
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No notifications yet</h3>
              <p className="text-muted-foreground">
                Notifications sent through this application will appear here
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="live-sessions" className="mt-4">
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No live sessions</h3>
              <p className="text-muted-foreground">
                Active live sessions for this application will appear here
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="developer" className="space-y-4 mt-4">
          <SubscriptionLinkGenerator appId={app.id} appName={app.name} />
        </TabsContent>
      </Tabs>

      {/* APNs Configuration Dialog */}
      <Dialog open={apnsDialogOpen} onOpenChange={setApnsDialogOpen}>
        <DialogContent className="glass-card border-white/10 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="h-5 w-5" />
              Configure APNs
            </DialogTitle>
            <DialogDescription>
              Enter your Apple Push Notification Service credentials
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="apns-key-id">Key ID *</Label>
              <Input
                id="apns-key-id"
                placeholder="e.g., ABC123DEFG"
                value={apnsKeyId}
                onChange={(e) => setApnsKeyId(e.target.value)}
                              />
              <p className="text-xs text-muted-foreground">
                Found in App Store Connect under Keys
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apns-team-id">Team ID *</Label>
              <Input
                id="apns-team-id"
                placeholder="e.g., TEAM123456"
                value={apnsTeamId}
                onChange={(e) => setApnsTeamId(e.target.value)}
                              />
              <p className="text-xs text-muted-foreground">
                Your Apple Developer Team ID
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apns-key-file">Authentication Key (.p8) *</Label>
              <div className="relative">
                <Input
                  id="apns-key-file"
                  type="file"
                  accept=".p8"
                  onChange={(e) => setApnsKeyFile(e.target.files?.[0] || null)}
                  className="file:bg-primary file:text-primary-foreground file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:cursor-pointer"
                />
              </div>
              {apnsKeyFile && (
                <p className="text-xs text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {apnsKeyFile.name}
                </p>
              )}
            </div>

            <Button
              variant="link"
              size="sm"
              className="text-primary p-0 h-auto"
              onClick={() => {
                setApnsDialogOpen(false)
                setApnsHelpOpen(true)
              }}
            >
              <HelpCircle className="h-4 w-4 mr-1" />
              Need help finding these credentials?
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApnsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApnsSubmit}
              disabled={configureApns.isPending || !apnsKeyId || !apnsTeamId || !apnsKeyFile}
            >
              {configureApns.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FCM Configuration Dialog */}
      <Dialog open={fcmDialogOpen} onOpenChange={setFcmDialogOpen}>
        <DialogContent className="glass-card border-white/10 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
              Configure FCM
            </DialogTitle>
            <DialogDescription>
              Upload your Firebase service account credentials
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fcm-file">Service Account JSON *</Label>
              <div className="relative">
                <Input
                  id="fcm-file"
                  type="file"
                  accept=".json"
                  onChange={(e) => setFcmFile(e.target.files?.[0] || null)}
                  className="file:bg-primary file:text-primary-foreground file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:cursor-pointer"
                />
              </div>
              {fcmFile && (
                <p className="text-xs text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {fcmFile.name}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Download from Firebase Console → Project Settings → Service Accounts
              </p>
            </div>

            <Button
              variant="link"
              size="sm"
              className="text-primary p-0 h-auto"
              onClick={() => {
                setFcmDialogOpen(false)
                setFcmHelpOpen(true)
              }}
            >
              <HelpCircle className="h-4 w-4 mr-1" />
              Need help finding these credentials?
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFcmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleFcmSubmit}
              disabled={configureFcm.isPending || !fcmFile}
            >
              {configureFcm.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* APNs Help Dialog */}
      <Dialog open={apnsHelpOpen} onOpenChange={setApnsHelpOpen}>
        <DialogContent className="glass-card border-white/10 sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="h-5 w-5" />
              How to Get APNs Credentials
            </DialogTitle>
            <DialogDescription>
              Follow these steps to obtain your Apple Push Notification credentials
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span>
                Go to Apple Developer Portal
              </h4>
              <p className="text-sm text-muted-foreground pl-8">
                Visit{' '}
                <a
                  href="https://developer.apple.com/account/resources/authkeys/list"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Apple Developer Account - Keys
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span>
                Create a New Key
              </h4>
              <ul className="text-sm text-muted-foreground pl-8 space-y-1 list-disc list-inside">
                <li>Click the "+" button to create a new key</li>
                <li>Enter a name for your key (e.g., "AlertWhale Push Key")</li>
                <li>Check "Apple Push Notifications service (APNs)"</li>
                <li>Click "Continue" and then "Register"</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">3</span>
                Download the Key
              </h4>
              <div className="text-sm text-muted-foreground pl-8 space-y-2">
                <p>Download the .p8 file - <strong className="text-warning">you can only download this once!</strong></p>
                <p>Note down the <strong>Key ID</strong> shown on the page.</p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">4</span>
                Find Your Team ID
              </h4>
              <p className="text-sm text-muted-foreground pl-8">
                Your Team ID is visible in the top-right of the Apple Developer portal, or in{' '}
                <a
                  href="https://developer.apple.com/account#MembershipDetailsCard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Membership Details
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">You'll need:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Key ID (10 character alphanumeric)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Team ID (10 character alphanumeric)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Authentication Key (.p8 file)
                </li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApnsHelpOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setApnsHelpOpen(false)
                setApnsDialogOpen(true)
              }}
            >
              Configure APNs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FCM Help Dialog */}
      <Dialog open={fcmHelpOpen} onOpenChange={setFcmHelpOpen}>
        <DialogContent className="glass-card border-white/10 sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
              How to Get FCM Credentials
            </DialogTitle>
            <DialogDescription>
              Follow these steps to obtain your Firebase Cloud Messaging credentials
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span>
                Go to Firebase Console
              </h4>
              <p className="text-sm text-muted-foreground pl-8">
                Visit{' '}
                <a
                  href="https://console.firebase.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Firebase Console
                  <ExternalLink className="h-3 w-3" />
                </a>{' '}
                and select your project (or create a new one)
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span>
                Navigate to Project Settings
              </h4>
              <ul className="text-sm text-muted-foreground pl-8 space-y-1 list-disc list-inside">
                <li>Click the gear icon next to "Project Overview"</li>
                <li>Select "Project settings"</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">3</span>
                Go to Service Accounts
              </h4>
              <ul className="text-sm text-muted-foreground pl-8 space-y-1 list-disc list-inside">
                <li>Click on the "Service accounts" tab</li>
                <li>Make sure "Firebase Admin SDK" is selected</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">4</span>
                Generate Private Key
              </h4>
              <div className="text-sm text-muted-foreground pl-8 space-y-2">
                <p>Click "Generate new private key" button</p>
                <p>A JSON file will be downloaded - <strong className="text-warning">keep this file secure!</strong></p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">You'll need:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Service Account JSON file
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                The JSON file contains your project ID, private key, and other credentials needed to send push notifications.
              </p>
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <p className="text-sm text-warning flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Security Note:</strong> Never share or commit this file to version control.
                  AlertWhale encrypts and securely stores your credentials.
                </span>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFcmHelpOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setFcmHelpOpen(false)
                setFcmDialogOpen(true)
              }}
            >
              Configure FCM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
